import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { deserializePlanIndicatorsToPlanIndices } from "../../functions/planIndicatorsSerialization";
import { formatDateAsMonthYear } from "../../functions/formatters";

const asText = (v) => (v == null || v === "" ? "—" : String(v));
const asNum = (v) => {
  if (v == null || v === "") return "—";
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "—";
};

const formatPeriod = (row) => {
  const created = row?.createdAt ?? row?.CreatedAt;
  if (created) {
    const formatted = formatDateAsMonthYear(created);
    if (formatted) return formatted;
  }
  return "—";
};

const normalizeIndices = (row) => {
  const direct = row?.indices || row?.planIndices;
  if (Array.isArray(direct) && direct.length) {
    return direct.filter((x) => x && !x._isDeleted);
  }
  return deserializePlanIndicatorsToPlanIndices(
    row?.indicators ?? row?.Indicators
  ).filter((x) => x && !x._isDeleted);
};

const indexName = (index) =>
  index?.index?.name ||
  index?.indexName ||
  (index?.indexId != null && String(index.indexId).trim() !== ""
    ? `Индекс ${index.indexId}`
    : "Показатель");

const researchCodeOf = (index) => {
  const code =
    index?.service?.code ||
    index?.serviceCode ||
    index?.researchCode ||
    null;
  return code ? String(code) : "";
};

/** Секция = лаборатория + группа (как в PlanDetail / «Количество проб»). */
const sectionKeyOf = (index) => {
  const deptId = index?.departmentId ?? index?.department?.id;
  const groupId = index?.groupId ?? index?.group?.id;
  const hasDept = deptId != null && String(deptId).trim() !== "";
  const hasGroup = groupId != null && String(groupId).trim() !== "";
  if (!hasDept && !hasGroup) return "_none";
  const d = hasDept ? String(deptId) : "_d";
  const g = hasGroup ? String(groupId) : "_g";
  return `${d}|${g}`;
};

const sectionLabelOf = (index) => {
  if (sectionKeyOf(index) === "_none") return "Без лаборатории";
  const dept =
    index?.departmentName || index?.department?.name || "Лаборатория";
  const grp =
    index?.groupName || index?.group?.name || index?.serviceGroup?.name || null;
  return grp ? `${dept} — ${grp}` : dept;
};

/** Уникальный ключ исследования внутри секции (лаборатория+группа). */
const researchKey = (index) => {
  const idPart =
    index?.indexId != null && String(index.indexId).trim() !== ""
      ? String(index.indexId)
      : "";
  const code = researchCodeOf(index);
  const name = indexName(index).trim().toLowerCase();
  return `r_${idPart}|${code}|${name}`;
};

/**
 * Группы: лаборатория+группа → исследования.
 * Количество проб (plannedPlan) — на одну группу.
 */
const resolveSectionGroups = (rows = []) => {
  const sections = new Map();
  (rows || []).forEach((row) => {
    normalizeIndices(row).forEach((index) => {
      const sk = sectionKeyOf(index);
      if (!sections.has(sk)) {
        sections.set(sk, {
          key: sk,
          label: sectionLabelOf(index),
          researches: new Map(),
        });
      }
      const section = sections.get(sk);
      const rk = researchKey(index);
      if (!section.researches.has(rk)) {
        section.researches.set(rk, {
          key: rk,
          indexId: index?.indexId ?? null,
          serviceId: index?.serviceId ?? index?.service?.id ?? null,
          name: indexName(index),
          code: researchCodeOf(index),
        });
      }
    });
  });

  return Array.from(sections.values())
    .map((section) => ({
      key: section.key,
      label: section.label,
      researches: Array.from(section.researches.values()).sort((a, b) =>
        a.name.localeCompare(b.name, "ru")
      ),
    }))
    .sort((a, b) => a.label.localeCompare(b.label, "ru"));
};

const pickIndex = (row, sectionKey, researchCol) => {
  const indices = normalizeIndices(row);
  return (
    indices.find(
      (item) =>
        sectionKeyOf(item) === sectionKey &&
        researchKey(item) === researchCol.key
    ) || null
  );
};

/** Количество проб на группу (plannedPlan секции лаборатория+группа). */
const sampleCountForSection = (row, sectionKey) => {
  const indices = normalizeIndices(row).filter(
    (item) => sectionKeyOf(item) === sectionKey
  );
  if (!indices.length) return null;
  const withValue = indices.find(
    (item) => item.plannedPlan != null && item.plannedPlan !== ""
  );
  return withValue?.plannedPlan ?? indices[0]?.plannedPlan ?? null;
};

/**
 * Сформированный план: строка = объект;
 * после «Местонахождение» — количество проб на группу;
 * справа исследования под заголовком лаборатории/группы
 * (подстолбцы: код / сумма; без «План (иссл.)»).
 */
export default function PlanDocumentFormedTable({
  plans = [],
  title = "Сформированный план",
}) {
  const sectionGroups = useMemo(() => resolveSectionGroups(plans), [plans]);

  const researchSubColCount = sectionGroups.reduce(
    (sum, section) => sum + section.researches.length * 2,
    0
  );
  /** 7 статических + по 1 «Кол-во проб» на группу + подстолбцы исследований */
  const colSpan = 7 + sectionGroups.length + researchSubColCount;

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Строка — объект плана; колонка «Период» — год/месяц объекта; после
        «Местонахождение» — количество проб на группу; справа — исследования
        под заголовком лаборатории/группы.
      </Typography>
      <TableContainer
        component={Box}
        sx={{
          maxHeight: "70vh",
          overflow: "auto",
          border: "1px solid #e0e0e0",
          borderRadius: 1,
        }}
      >
        <Table
          size="small"
          stickyHeader
          sx={{
            minWidth: Math.max(
              1000,
              700 + sectionGroups.length * 120 + researchSubColCount * 110
            ),
          }}
        >
          <TableHead>
            {/* Ряд 1: статика + заголовки лаборатория — группа */}
            <TableRow>
              <TableCell
                sx={{ bgcolor: "#f5f5f5", fontWeight: 700, minWidth: 70 }}
                rowSpan={3}
              >
                Id
              </TableCell>
              <TableCell
                sx={{ bgcolor: "#eef6f1", fontWeight: 700, minWidth: 120 }}
                rowSpan={3}
              >
                Период
              </TableCell>
              <TableCell
                sx={{ bgcolor: "#f5f5f5", fontWeight: 700, minWidth: 140 }}
                rowSpan={3}
              >
                Район/город
              </TableCell>
              <TableCell
                sx={{ bgcolor: "#f5f5f5", fontWeight: 700, minWidth: 220 }}
                rowSpan={3}
              >
                Субъект
              </TableCell>
              <TableCell
                sx={{ bgcolor: "#f5f5f5", fontWeight: 700, minWidth: 120 }}
                rowSpan={3}
              >
                ИИН/БИН
              </TableCell>
              <TableCell
                sx={{ bgcolor: "#f5f5f5", fontWeight: 700, minWidth: 220 }}
                rowSpan={3}
              >
                Объект
              </TableCell>
              <TableCell
                sx={{ bgcolor: "#f5f5f5", fontWeight: 700, minWidth: 180 }}
                rowSpan={3}
              >
                Местонахождение
              </TableCell>
              {sectionGroups.length === 0 ? (
                <TableCell
                  sx={{ bgcolor: "#e8eef8", fontWeight: 700 }}
                  rowSpan={3}
                >
                  Лаборатория
                </TableCell>
              ) : (
                sectionGroups.map((section) => (
                  <TableCell
                    key={section.key}
                    align="center"
                    colSpan={1 + section.researches.length * 2}
                    sx={{ bgcolor: "#dce8f5", fontWeight: 700 }}
                  >
                    Лаборатория: {section.label}
                  </TableCell>
                ))
              )}
            </TableRow>

            {/* Ряд 2: кол-во проб на группу + названия исследований */}
            {sectionGroups.length > 0 && (
              <TableRow>
                {sectionGroups.map((section) => (
                  <React.Fragment key={`${section.key}_mid`}>
                    <TableCell
                      align="center"
                      rowSpan={2}
                      sx={{
                        bgcolor: "#eef6f1",
                        fontWeight: 700,
                        minWidth: 110,
                      }}
                    >
                      Количество проб
                    </TableCell>
                    {section.researches.map((g) => (
                      <TableCell
                        key={g.key}
                        align="center"
                        colSpan={2}
                        sx={{ bgcolor: "#e8eef8", fontWeight: 700 }}
                      >
                        {g.name}
                        {g.code ? ` (${g.code})` : ""}
                      </TableCell>
                    ))}
                  </React.Fragment>
                ))}
              </TableRow>
            )}

            {/* Ряд 3: код / сумма */}
            {sectionGroups.length > 0 && (
              <TableRow>
                {sectionGroups.map((section) =>
                  section.researches.map((g) => (
                    <React.Fragment key={`${g.key}_sub`}>
                      <TableCell
                        align="center"
                        sx={{ bgcolor: "#fff8e1", minWidth: 100 }}
                      >
                        Код исслед.
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{ bgcolor: "#e8eef8", minWidth: 100 }}
                      >
                        План (сумма)
                      </TableCell>
                    </React.Fragment>
                  ))
                )}
              </TableRow>
            )}
          </TableHead>
          <TableBody>
            {!plans.length ? (
              <TableRow>
                <TableCell colSpan={Math.max(colSpan, 8)}>
                  <Typography color="text.secondary">
                    Нет объектов в сформированном плане.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              plans.map((row) => {
                const planId = row.id ?? row.Id;
                return (
                  <TableRow key={planId} hover>
                    <TableCell>{planId}</TableCell>
                    <TableCell>{formatPeriod(row)}</TableCell>
                    <TableCell>{asText(row.districtCity)}</TableCell>
                    <TableCell>{asText(row.inspectedEntityName)}</TableCell>
                    <TableCell>{asText(row.individualOrBusinessId)}</TableCell>
                    <TableCell>{asText(row.inspectedObjectName)}</TableCell>
                    <TableCell>{asText(row.location)}</TableCell>
                    {sectionGroups.length === 0 ? (
                      <TableCell>—</TableCell>
                    ) : (
                      sectionGroups.map((section) => (
                        <React.Fragment key={`${planId}_${section.key}`}>
                          <TableCell
                            align="center"
                            sx={{ bgcolor: "#f7fbf8", fontWeight: 600 }}
                          >
                            {asNum(sampleCountForSection(row, section.key))}
                          </TableCell>
                          {section.researches.map((g) => {
                            const idx = pickIndex(row, section.key, g);
                            return (
                              <React.Fragment
                                key={`${planId}_${section.key}_${g.key}`}
                              >
                                <TableCell
                                  align="center"
                                  sx={{
                                    bgcolor: idx ? "#fffef5" : undefined,
                                  }}
                                >
                                  {idx
                                    ? asText(researchCodeOf(idx) || "—")
                                    : "—"}
                                </TableCell>
                                <TableCell align="center">
                                  {idx ? asNum(idx.plannedSum) : "—"}
                                </TableCell>
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      ))
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
